import { MutableRefObject, useState, useRef, useEffect, useCallback } from 'react';

type useElementVisibleParams = {
  offsetTop?: number;
  offsetBottom?: number;
  enableScrollListener?: boolean;
};

/**
 * **NOTE:**
 *
 * Needs `popperModifiers={{ preventOverflow: { enabled: false }, hide: { enabled: false } }}`
 * on the tooltip to work if using on a tooltip
 */
export default function useElementVisible<T>({
  offsetTop = 0,
  offsetBottom = 0,
  enableScrollListener = false,
}: useElementVisibleParams): [boolean, MutableRefObject<T>] {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  const onScroll = useCallback(() => {
    if (!ref.current) {
      setIsVisible(false);
      return;
    }
    const top = ref.current.getBoundingClientRect().top;
    setIsVisible(top + offsetTop >= 0 && top - offsetBottom <= window.innerHeight);
  }, [offsetBottom, offsetTop]);

  useEffect(() => {
    if (enableScrollListener) document.addEventListener('scroll', onScroll, true);
    else document.removeEventListener('scroll', onScroll, true);
  }, [enableScrollListener, onScroll]);

  return [isVisible, ref];
}
